import type { ResolvedMonospaceSpace } from '#layers/data-model/types/schema/resolved-schema';
import { useOverlay } from '@nuxt/ui/composables';
import { defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from '#app/composables/router';
import { useStore, useToastResultHandling } from '#imports';
import { showConfirmationDialog } from '#layers/ui/utils/show-confirmation-dialog';

const LazySpaceCreate = defineAsyncComponent(() => import('#layers/spaces/components/Space/SpaceCreate.vue'));

export function useSpaceEdit() {
	const { t } = useI18n();
	const route = useRoute();
	const router = useRouter();
	const store = useStore();
	const overlay = useOverlay();

	function createSpace() {
		const modal = overlay.create(LazySpaceCreate);
		modal.open();
	}

	function openSpaceEdit() {
		router.push({
			...route,
			query: {
				...route.query,
				buildSpace: 'true',
			},
		});
	}

	const deleteToast = useToastResultHandling({
		success: {
			title: t('space.delete_space_success'),
		},
		error: {
			title: t('space.delete_space_error'),
		},
	});

	async function deleteSpace(space: ResolvedMonospaceSpace) {
		if (await showConfirmationDialog({
			props: {
				title: t('space.confirm_delete_space.title', { name: space.title }),
				description: t('space.confirm_delete_space.description'),
				confirm: {
					label: t('space.delete_space'),
					color: 'error',
				},
			},
		})) {
			try {
				await store.MonospaceSpace.delete(space.id);

				deleteToast.onSuccess();

				router.replace({
					name: 'project-home',
				});
			}
			catch (error) {
				deleteToast.onError(error);
			}
		}
	}

	return {
		openSpaceEdit,
		createSpace,
		deleteSpace,
	};
}
